import { Card, FormGroup, InputWithUnit } from '../ui/Card';
import { MN_COUNTIES } from '../../lib/mn-data';
import type { CalculatorInputs, LoanType, CreditScoreRange } from '../../lib/types';
import { fmtInt } from '../../lib/format';

interface Props {
  inputs: CalculatorInputs;
  updateInput: <K extends keyof CalculatorInputs>(key: K, value: CalculatorInputs[K]) => void;
  loanAmount: number;
  collapsible?: boolean;
  summary?: string;
  isOpen?: boolean;
  onToggle?: () => void;
}

export function MortgageInputsCard({ inputs, updateInput, loanAmount, collapsible, summary, isOpen, onToggle }: Props) {
  return (
    <Card title="Mortgage Details" collapsible={collapsible} summary={summary} isOpen={isOpen} onToggle={onToggle}>
      <div className="form-row">
        <FormGroup label="Home Price" htmlFor="homePrice">
          <InputWithUnit
            unit="$"
            id="homePrice"
            value={inputs.homePrice}
            onChange={v => updateInput('homePrice', v)}
            min={0}
            step={5000}
          />
        </FormGroup>
        <FormGroup label="Down Payment %" htmlFor="downPaymentPct">
          <InputWithUnit
            unit="%"
            unitPosition="right"
            id="downPaymentPct"
            value={inputs.downPaymentPct}
            onChange={v => updateInput('downPaymentPct', v)}
            min={0}
            max={100}
            step={0.5}
          />
        </FormGroup>
      </div>
      <div className="form-row">
        <FormGroup label="Down Payment Amount" htmlFor="downPaymentAmt">
          <InputWithUnit
            unit="$"
            id="downPaymentAmt"
            value={inputs.downPaymentAmt}
            onChange={v => updateInput('downPaymentAmt', v)}
            min={0}
            step={1000}
          />
        </FormGroup>
        <FormGroup label="Loan Amount">
          <InputWithUnit unit="$" value={fmtInt(loanAmount)} onChange={() => {}} readOnly />
        </FormGroup>
      </div>
      <div className="form-row">
        <FormGroup label="Interest Rate %" htmlFor="interestRate">
          <InputWithUnit
            unit="%"
            unitPosition="right"
            id="interestRate"
            value={inputs.interestRate}
            onChange={v => updateInput('interestRate', v)}
            min={0}
            max={15}
            step={0.125}
          />
        </FormGroup>
        <FormGroup label="Loan Term" htmlFor="loanTerm">
          <select
            id="loanTerm"
            value={inputs.loanTerm}
            onChange={e => updateInput('loanTerm', parseInt(e.target.value))}
          >
            <option value={30}>30 years</option>
            <option value={25}>25 years</option>
            <option value={20}>20 years</option>
            <option value={15}>15 years</option>
            <option value={10}>10 years</option>
          </select>
        </FormGroup>
      </div>
      <div className="form-row">
        <FormGroup label="Loan Type" htmlFor="loanType">
          <select
            id="loanType"
            value={inputs.loanType}
            onChange={e => updateInput('loanType', e.target.value as LoanType)}
          >
            <option value="conventional">Conventional</option>
            <option value="fha">FHA</option>
            <option value="va">VA</option>
            <option value="usda">USDA</option>
          </select>
        </FormGroup>
        <FormGroup label="Credit Score" htmlFor="creditScore">
          <select
            id="creditScore"
            value={inputs.creditScore}
            onChange={e => updateInput('creditScore', e.target.value as CreditScoreRange)}
          >
            <option value="760">760+ (Excellent)</option>
            <option value="740">740–759</option>
            <option value="720">720–739</option>
            <option value="700">700–719</option>
            <option value="680">680–699</option>
            <option value="660">660–679</option>
            <option value="640">640–659</option>
          </select>
        </FormGroup>
      </div>
      <div className="form-row">
        <FormGroup label="MN County" htmlFor="mnCounty">
          <select
            id="mnCounty"
            value={inputs.mnCounty}
            onChange={e => updateInput('mnCounty', e.target.value)}
          >
            {MN_COUNTIES.map(c => (
              <option key={c.label} value={c.value}>{c.label}</option>
            ))}
          </select>
        </FormGroup>
        <FormGroup label="Property Tax Rate %" htmlFor="propertyTaxRate">
          <InputWithUnit
            unit="%"
            unitPosition="right"
            id="propertyTaxRate"
            value={inputs.propertyTaxRate}
            onChange={v => updateInput('propertyTaxRate', v)}
            min={0}
            max={5}
            step={0.01}
          />
        </FormGroup>
      </div>
      <div className="form-row">
        <FormGroup label="Home Insurance (Annual)" htmlFor="homeInsurance">
          <InputWithUnit
            unit="$"
            id="homeInsurance"
            value={inputs.homeInsurance}
            onChange={v => updateInput('homeInsurance', v)}
            min={0}
            step={100}
          />
        </FormGroup>
        <FormGroup label="HOA Fees (Monthly)" htmlFor="hoaFees">
          <InputWithUnit
            unit="$"
            id="hoaFees"
            value={inputs.hoaFees}
            onChange={v => updateInput('hoaFees', v)}
            min={0}
            step={25}
          />
        </FormGroup>
      </div>
    </Card>
  );
}
